/**
 * Armada Key Features Carousel
 * Pointer drag-to-scroll on the cards, plus a scroll indicator that doubles as
 * a scrollbar (click to jump, drag the thumb). Touch keeps native scrolling.
 */
(() => {
  'use strict';

  /* Pointer travel before a press turns into a drag rather than a click. */
  const DRAG_THRESHOLD = 4;

  /* Dragging and selecting text are the same gesture, so the card splits the two:
     press the artwork to scroll, press the copy to select it. */
  const TEXT_SELECTORS = '.armada-key-features__card-title, .armada-key-features__card-description';

  class ArmadaKeyFeatures {
    constructor(container) {
      this.container = container;
      this.scrollContainer = container.querySelector('.armada-key-features__scroll-container');
      this.indicator = container.querySelector('.armada-key-features__scroll-indicator[data-scroll-for]');
      this.track = this.indicator?.querySelector('.armada-key-features__scroll-track');
      this.thumb = this.indicator?.querySelector('.armada-key-features__scroll-thumb');
      this.scrollHandler = () => this.updateScrollIndicator();
      this.resizeHandler = () => this.updateControls();
      this.keydownHandler = (event) => this.handleKeydown(event);
      this.pointerDownHandler = (event) => this.handlePointerDown(event);
      this.pointerMoveHandler = (event) => this.handlePointerMove(event);
      this.pointerUpHandler = (event) => this.handlePointerUp(event);
      this.dragStartHandler = (event) => this.handleDragStart(event);
      this.clickHandler = (event) => this.handleClick(event);
      this.trackPointerDownHandler = (event) => this.handleTrackPointerDown(event);
      this.trackPointerMoveHandler = (event) => this.handleTrackPointerMove(event);
      this.trackPointerUpHandler = (event) => this.handleTrackPointerUp(event);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollable = false;

      this.pointerId = null;
      this.isDragging = false;
      this.suppressClick = false;
      this.trackPointerId = null;
      this.trackGrabOffset = 0;
      this.thumbWidth = 0;
      this.thumbOffset = 0;

      if (!this.scrollContainer) return;

      this.init();
    }

    init() {
      this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.scrollContainer.addEventListener('keydown', this.keydownHandler);
      this.scrollContainer.addEventListener('pointerdown', this.pointerDownHandler);
      this.scrollContainer.addEventListener('dragstart', this.dragStartHandler);
      this.scrollContainer.addEventListener('click', this.clickHandler, true);
      this.track?.addEventListener('pointerdown', this.trackPointerDownHandler);
      this.track?.addEventListener('keydown', this.keydownHandler);
      window.addEventListener('resize', this.resizeHandler, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.scrollContainer);
      }

      this.updateControls();
    }

    destroy() {
      if (!this.scrollContainer) return;

      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollContainer.removeEventListener('keydown', this.keydownHandler);
      this.scrollContainer.removeEventListener('pointerdown', this.pointerDownHandler);
      this.scrollContainer.removeEventListener('dragstart', this.dragStartHandler);
      this.scrollContainer.removeEventListener('click', this.clickHandler, true);
      this.track?.removeEventListener('pointerdown', this.trackPointerDownHandler);
      this.track?.removeEventListener('keydown', this.keydownHandler);
      window.removeEventListener('resize', this.resizeHandler);
      this.stopPointerTracking();
      this.stopTrackPointerTracking();
      this.setDragging(false);
      this.resizeObserver?.disconnect();
    }

    /**
     * Reads the actual first-card width + gallery gap from the DOM so the
     * step stays correct across responsive breakpoints.
     */
    getScrollStep() {
      const gallery = this.scrollContainer.querySelector('.armada-key-features__gallery');
      const card = gallery?.querySelector('.armada-key-features__card');
      if (!card || !gallery) return this.scrollContainer.clientWidth;

      const cardWidth = card.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(gallery).columnGap || getComputedStyle(gallery).gap) || 0;
      return cardWidth + gap;
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const step = this.getScrollStep();
      const delta = event.key === 'ArrowLeft' ? -step : step;
      this.scrollContainer.scrollBy({ left: delta, behavior: this.scrollBehavior() });
    }

    scrollBehavior() {
      return this.prefersReducedMotion ? 'auto' : 'smooth';
    }

    /* Mandatory snap and smooth scrolling both fight per-frame scrollLeft writes,
       so both are switched off for the duration of a drag. */
    setDragging(isDragging) {
      this.scrollContainer.classList.toggle(
        'armada-key-features__scroll-container--dragging',
        isDragging
      );
      this.indicator?.classList.toggle(
        'armada-key-features__scroll-indicator--dragging',
        isDragging
      );
    }

    handlePointerDown(event) {
      /* Touch already scrolls natively (with momentum); drag-to-scroll is the
         mouse/pen affordance, since the scrollbar is hidden by design. */
      if (event.pointerType === 'touch' || event.button !== 0 || !this.scrollable) return;

      /* Titles and descriptions stay selectable, so a press there is left alone. */
      if (event.target.closest?.(TEXT_SELECTORS)) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartScrollLeft = this.scrollContainer.scrollLeft;
      this.isDragging = false;
      this.suppressClick = false;
      /* Capture keeps the release reachable even outside the window; cards hold
         no links, so retargeting the click does no harm. */
      this.scrollContainer.setPointerCapture?.(event.pointerId);

      window.addEventListener('pointermove', this.pointerMoveHandler, { passive: false });
      window.addEventListener('pointerup', this.pointerUpHandler);
      window.addEventListener('pointercancel', this.pointerUpHandler);
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const walk = event.clientX - this.pointerStartX;

      if (!this.isDragging) {
        if (Math.abs(walk) < DRAG_THRESHOLD) return;

        this.isDragging = true;
        this.suppressClick = true;
        this.setDragging(true);
        document.getSelection()?.removeAllRanges();
      }

      event.preventDefault();
      this.scrollContainer.scrollLeft = this.pointerStartScrollLeft - walk;
    }

    handlePointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      this.stopPointerTracking();

      if (!this.isDragging) return;

      this.isDragging = false;
      /* Restoring mandatory snap lands the gallery on the nearest card. */
      this.setDragging(false);
    }

    stopPointerTracking() {
      this.pointerId = null;
      window.removeEventListener('pointermove', this.pointerMoveHandler, { passive: false });
      window.removeEventListener('pointerup', this.pointerUpHandler);
      window.removeEventListener('pointercancel', this.pointerUpHandler);
    }

    handleDragStart(event) {
      /* Cards are mostly images — the browser's native drag ghost would
         otherwise hijack the gesture as soon as the pointer moves. */
      if (this.pointerId !== null) event.preventDefault();
    }

    handleClick(event) {
      if (!this.suppressClick) return;

      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    handleTrackPointerDown(event) {
      if (event.button !== 0 || !this.scrollable || !this.track) return;

      /* Keeps the press from starting a text selection on the section. */
      event.preventDefault();

      const pointerX = event.clientX - this.track.getBoundingClientRect().left;
      const grabbedThumb =
        pointerX >= this.thumbOffset && pointerX <= this.thumbOffset + this.thumbWidth;

      this.trackPointerId = event.pointerId;
      /* Grabbing the thumb keeps its offset under the pointer; clicking the bare
         track centres the thumb on the click instead. */
      this.trackGrabOffset = grabbedThumb ? pointerX - this.thumbOffset : this.thumbWidth / 2;
      this.track.setPointerCapture?.(event.pointerId);
      this.setDragging(true);

      window.addEventListener('pointermove', this.trackPointerMoveHandler, { passive: false });
      window.addEventListener('pointerup', this.trackPointerUpHandler);
      window.addEventListener('pointercancel', this.trackPointerUpHandler);

      if (!grabbedThumb) {
        this.scrollToThumbOffset(pointerX - this.trackGrabOffset, this.scrollBehavior());
      }
    }

    handleTrackPointerMove(event) {
      if (event.pointerId !== this.trackPointerId || !this.track) return;

      event.preventDefault();
      const pointerX = event.clientX - this.track.getBoundingClientRect().left;
      this.scrollToThumbOffset(pointerX - this.trackGrabOffset, 'auto');
    }

    handleTrackPointerUp(event) {
      if (event.pointerId !== this.trackPointerId) return;

      this.stopTrackPointerTracking();
      this.setDragging(false);
    }

    stopTrackPointerTracking() {
      this.trackPointerId = null;
      window.removeEventListener('pointermove', this.trackPointerMoveHandler, { passive: false });
      window.removeEventListener('pointerup', this.trackPointerUpHandler);
      window.removeEventListener('pointercancel', this.trackPointerUpHandler);
    }

    /** Maps a thumb position on the track back onto the gallery's scroll range. */
    scrollToThumbOffset(thumbOffset, behavior) {
      const maxThumbOffset = this.track.offsetWidth - this.thumbWidth;
      const maxScroll = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
      if (maxThumbOffset <= 0 || maxScroll <= 0) return;

      const ratio = Math.min(Math.max(thumbOffset / maxThumbOffset, 0), 1);
      this.scrollContainer.scrollTo({ left: ratio * maxScroll, behavior });
    }

    /**
     * Overflow of the *cards only*. The gallery's trailing
     * `padding-right: var(--slide-inset)` is excluded on purpose — at 1920px that
     * inset is 240px, so scrollWidth on its own reports ~224px of "overflow" even
     * when every card is fully in view.
     */
    getCardOverflow() {
      const cards = this.scrollContainer.querySelectorAll('.armada-key-features__card');
      const lastCard = cards[cards.length - 1];
      if (!lastCard) return 0;

      const containerLeft = this.scrollContainer.getBoundingClientRect().left;
      const cardsRight =
        lastCard.getBoundingClientRect().right - containerLeft + this.scrollContainer.scrollLeft;

      return cardsRight - this.scrollContainer.clientWidth;
    }

    isScrollable() {
      return this.getCardOverflow() > 1;
    }

    updateControlsVisibility(isScrollable) {
      this.scrollContainer.classList.toggle(
        'armada-key-features__scroll-container--locked',
        !isScrollable
      );

      /* A region you can't scroll shouldn't be a tab stop. */
      if (isScrollable) {
        this.scrollContainer.setAttribute('tabindex', '0');
      } else {
        this.scrollContainer.removeAttribute('tabindex');
        this.scrollContainer.scrollLeft = 0;
      }

      this.indicator?.classList.toggle(
        'armada-key-features__scroll-indicator--hidden',
        !isScrollable
      );
    }

    updateControls() {
      this.scrollable = this.isScrollable();
      this.updateControlsVisibility(this.scrollable);
      this.updateScrollIndicator();
    }

    updateScrollIndicator() {
      /* Cached rather than re-measured: this runs on every scroll event, and the
         track has no width to measure while the indicator is hidden anyway. */
      if (!this.thumb || !this.track || !this.scrollable) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0) {
        this.thumbWidth = this.track.offsetWidth;
        this.thumbOffset = 0;
        this.thumb.style.width = '100%';
        this.thumb.style.transform = 'translateX(0)';
        this.track.setAttribute('aria-valuenow', '0');
        return;
      }

      const thumbMinRaw = parseFloat(
        getComputedStyle(this.container).getPropertyValue('--kf-scroll-thumb-min')
      );
      const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const thumbMin = Number.isFinite(thumbMinRaw) ? thumbMinRaw * remInPx : 24;

      const trackWidth = this.track.offsetWidth;
      const ratio = clientWidth / scrollWidth;
      const scrollRatio = scrollLeft / maxScroll;

      this.thumbWidth = Math.max(trackWidth * ratio, thumbMin);
      this.thumbOffset = scrollRatio * (trackWidth - this.thumbWidth);

      this.thumb.style.width = `${this.thumbWidth}px`;
      this.thumb.style.transform = `translateX(${this.thumbOffset}px)`;
      this.track.setAttribute('aria-valuenow', `${Math.round(scrollRatio * 100)}`);
    }
  }

  const keyFeaturesInstances = new WeakMap();

  const initSection = (section) => {
    if (!section || keyFeaturesInstances.has(section)) return;

    const instance = new ArmadaKeyFeatures(section);
    keyFeaturesInstances.set(section, instance);
  };

  const destroySection = (section) => {
    if (!section) return;

    const instance = keyFeaturesInstances.get(section);
    if (!instance) return;

    instance.destroy();
    keyFeaturesInstances.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll('.armada-key-features').forEach((section) => {
      initSection(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.matches?.('.armada-key-features')
        ? event.target
        : event.target.querySelector('.armada-key-features');

      if (section) initSection(section);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.matches?.('.armada-key-features')
        ? event.target
        : event.target.querySelector('.armada-key-features');

      if (section) destroySection(section);
    });
  }
})();
